import { apiResolve } from 'frontend/router';
import { getData } from 'frontend/utils/ajax';

export type SoundAction =
  | 'continue'
  | 'correct'
  | 'drag-released'
  | 'drag-started'
  | 'endstate'
  | 'hint'
  | 'incorrect'
  | 'lightweight-choice'
  | 'skip'
  | 'start-lesson'
  | 'toggle-off'
  | 'toggle-on';

const soundActions: Map<SoundAction, { audioBuffer?: AudioBuffer; arrayBuffer: ArrayBuffer }> =
  new Map();
let context: AudioContext | undefined;

function makeNewAudioContext(): AudioContext | undefined {
  let audioContext;

  if (window.AudioContext) {
    audioContext = new AudioContext();
  } else if ((window as any).webkitAudioContext) {
    audioContext = new (window as any).webkitAudioContext();
  }

  return audioContext;
}

export async function playActionSound(action: SoundAction): Promise<void> {
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

  if (isSafari) {
    // Creating a new context and new buffers is needed to avoid issues on safari mobile.
    // Using the same context several times was causing sounds to play with a delay or inconsistently.
    resetContextAndAudioBuffers();
  }

  const actionToPlay = soundActions.get(action);

  if (actionToPlay) {
    const audioBufferToPlay = actionToPlay.audioBuffer;

    if (audioBufferToPlay) {
      const source = new AudioBufferSourceNode(context!, { buffer: audioBufferToPlay });
      source.connect(context!.destination);
      source.start();
    }
  }
}

async function resetContextAndAudioBuffers(): Promise<void> {
  context = makeNewAudioContext();

  soundActions.forEach(async ({ arrayBuffer }, action) => {
    const audioBuffer = await context!.decodeAudioData(arrayBuffer.slice(0));

    soundActions.set(action, { arrayBuffer, audioBuffer });
  });
}

export async function loadSounds(): Promise<void> {
  context = makeNewAudioContext();

  const route = apiResolve({ name: 'sound_actions' });

  try {
    const actionsAndUrls = (await getData(route.href)).results.sound_actions as Array<{
      action: SoundAction;
      sound_url: string;
    }>;

    actionsAndUrls.forEach(async ({ action, sound_url }) => {
      const arrayBuffer = await (await fetch(sound_url)).arrayBuffer();
      const audioBuffer = await context!.decodeAudioData(arrayBuffer);
      soundActions.set(action, { arrayBuffer, audioBuffer });
    });
  } catch (e) {
    // Ignore any problems loading sounds
    return;
  }
}
